<template>
    <div
      v-if="show"
      @click.stop="stopCloseHandler"
      class="event-list__modal"
    >
      <div class="event-list__modal-wrapper">
        <div class="event-list__modal-head">
            <h3 class="event-list__modal-head-title">
              <translated-text-component translation="modal_event_list_title" />
            </h3>
            <v-btn 
              @click="closeEventListModal()"
              class="event-list__modal-close-btn"
              icon="mdi mdi-close"
              variant="text"
              aria-label="Zamknij okno"
            >
            </v-btn>
            <div class="event-list__filters-wrapper">
              <div class="event-list__kind-filter">
                <v-btn 
                  v-for="category in eventCategories"
                  :key="category.key"
                  @click="updateEventCategorySwitcher(category.key, !eventCategorySwichers[category.key])"
                  :class="{'--inactive': !eventCategorySwichers[category.key]}"
                  size="small"
                  color="primary" 
                  class="event-list__kind-filter-btn"
                  aria-label="Przełącz kategorię zdarzeń {{ category.key }}"
                >
                  <div 
                    v-if="category.icon_code"
                    class="event-list__kind-filter-btn-icon"
                    :style="`mask: url(data:image/svg+xml;base64,${category.icon_code}) no-repeat center; mask-size: contain;`"
                  ></div>
                  <div class="event-list__kind-filter-label">
                    <!-- {{ category.key }} -->
                    <translated-text-component :translation="`EVENT_CATEGORY_NAME_${category.key}`" />
                  </div>
                </v-btn>
              </div>
              <div class="event-list__calendar-filter">
                <v-btn @click="prevDay()" class="mx-2" color="primary" aria-label="Poprzedni dzień">
                  <v-icon color="buttonIcons"> mdi-arrow-left-bold </v-icon>
                </v-btn>
                <div class="event-list__calendar-filter-box">
                  <div class="event-list-calendar-filer-feature">
                    <v-menu
                      v-model="showDatePickerMenu"
                      :close-on-content-click="false"
                      offset-y
                    >
                      <template #activator="{ props }">
                        <v-text-field
                          class="no-msg arrival-select"
                          :value="formattedDate"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          solo
                          density="compact"
                          hide-details
                          v-bind="props"
                          size
                        ></v-text-field>
                      </template>

                      <v-date-picker
                        v-model="currentDateTime"
                        @input="onDateSelected"
                        @update:model-value="showDatePickerMenu = false"
                        color="primary"
                        width="300"
                      ></v-date-picker>
                    </v-menu>
                  </div>
                </div>
                <v-btn @click="nextDay()" class="mx-2" color="primary" aria-label="Następny dzień">
                  <v-icon color="buttonIcons"> mdi-arrow-right-bold </v-icon>
                </v-btn>
              </div>
            </div>
        </div>
        <div class="event-list__modal-scroll-container">
            <div class="event-list__modal-scroll-content">
             <div
                class="event-list__day"
                v-for="date in orderedDays"
                :key="date"
              >
                <div class="event-list__date">
                  {{date}}
                </div>
                <Event
                  v-for="singleEvent in groupedEvents[date]"
                  :key="singleEvent.name"
                  :data="singleEvent"
                />
              </div>
            </div>
        </div>
      </div>
    </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, Ref, onMounted, ref, watch,
} from 'vue';
import store from '@/store/index';
import { groupBy, keys } from 'lodash';
import {
  parseJSON, parse, format, isValid,
} from 'date-fns';
import { pl } from 'date-fns/locale';
import { IModalData } from '@/services/layers/IModalData';
import Event from '@/components/structural/Event.vue';
import { AxiosResponse } from 'axios';
import ApiService from '@/services/ApiService';
import moment from 'moment';
import { IEventCategory } from '@/interfaces/IEventCategory';

const EventsListModal: object = defineComponent({
  components: {
    Event,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
      name: 'show',
    },
  },
  name: 'EventsListModal',
  setup() {
    // const currentDateTime = ref((new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10));
    const showDatePickerMenu = ref(false); // Controls menu visibility
    const currentDateTime = ref<Date | null>(new Date()); // Use Date objects internally

    // Computed property to format the date for display
    const formattedDate = computed(() => {
      return currentDateTime.value
        ? currentDateTime.value.toLocaleDateString()
        : '';
    });

    // Handler for date selection
    const onDateSelected = () => {
      showDatePickerMenu.value = false; // Close the menu
    };
    const eventList = ref<any[]>([]);
    const filteredEventList = ref<any[]>([]);

    interface IEventCategorySwichers {
      [key: string]: boolean
    }
    
    const eventCategories: Ref<IEventCategory[]> = computed(() => 
      store.state.eventCategories.filter((category: IEventCategory) => category.value === true)
    );

    const eventCategorySwichers: Ref<IEventCategorySwichers> = ref(
      store.state.eventCategories.reduce((acc: any, item: any) => {
        acc[item.key] = item.value;
        return acc;
      }, {})
    );

    // Method to update a single switcher
    const updateEventCategorySwitcher = (key: string, value: any) => {
        eventCategorySwichers.value[key] = value;
    };
    
    const prevDay = () => {
      if (currentDateTime.value) {
        const updatedDate = new Date(currentDateTime.value);
        updatedDate.setDate(updatedDate.getDate() - 1);
        currentDateTime.value = updatedDate;
      }
    };

    const nextDay = () => {
      if (currentDateTime.value) {
        const updatedDate = new Date(currentDateTime.value);
        updatedDate.setDate(updatedDate.getDate() + 1);
        currentDateTime.value = updatedDate;
      }
    };

    const eventsFilterBySelectedCategories = () => {
      const selectedCategoriesKeys = Object.keys(eventCategorySwichers.value).filter((category: string) => {
        return eventCategorySwichers.value[category];
      });
      
      filteredEventList.value = eventList.value.filter((event: any) => {
        return selectedCategoriesKeys.includes(event.properties.category.event_code);
      });
    };

    const getEvents = () => {
      const formatedDateForView = moment(currentDateTime.value).format().toString();

      return ApiService.fetchEventsByTime(encodeURIComponent(formatedDateForView))
          .then((res: AxiosResponse<any>) => {
              eventList.value = res.data.features;
              // filteredEventList.value = res.data.features;
              eventsFilterBySelectedCategories();
          })
          .catch((reason => console.error(reason)));
    };

    const closeEventListModal = () => {
      store.commit('closeEventListModal');
    };

    const stopCloseHandler = (e: any) => {
      e.stopPropagation();
    };

    const groupedEvents: Ref<any> = computed(
      () => groupBy(
        filteredEventList.value,
        (o :any) => {
          let date: Date = parseJSON(o.properties.start_time);

          if (!isValid(date)) {
            date = parseJSON(o.properties.create_time);
          }
          return format(date, 'yyyy-MM-dd');
        },
      ),
    );

    const orderedDays: Ref<any> = computed(
      () => keys(groupedEvents.value).sort().reverse(),
    );

    onMounted(() => {
      getEvents();

      const body = document.querySelector('body');

      if (body) {
        body.addEventListener('click', () => {
          // store.commit('closeEventListModal');
        });
      }
    });

    watch(() => currentDateTime.value, () => {
      getEvents();
    });

    watch(() => eventCategorySwichers.value, () => {
      eventsFilterBySelectedCategories();
    },
    { 
      deep: true,
    });

    return {
      eventCategories,
      eventCategorySwichers,
      groupedEvents,
      orderedDays,
      eventList,
      filteredEventList,
      currentDateTime,
      formattedDate,
      showDatePickerMenu,
      closeEventListModal,
      stopCloseHandler,
      prevDay,
      nextDay,
      onDateSelected,
      eventsFilterBySelectedCategories,
      updateEventCategorySwitcher,
    };
  },
});
export default EventsListModal;
</script>

<style lang="scss">
  .event-list__modal {
    font-family: $main-font-family;
    position: absolute;
    width: rem(770px);
    max-width: 100%;
    padding: rem(25px) rem(5px);
    top: rem(134px);
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    border-radius: rem(9px);
    color: $main-font-color;
    background-color: $main-bg-color;
    border: rem(2px) solid rgba(#F1F1F1, 0.97);
    box-shadow: 0 0 10px 0px rgba(#000000, 0.3);
    height: calc(84vh - 82px);

    @include width-until($mobile) {
      top: 50%;
      transform: translate(-50%, -50%);
    }

    .dark-theme & {
      background-color: $dark-static;
      border: rem(2px) solid $yellow-static;
    }

    &-wrapper {
      display: flex;
      flex-flow: column;
      height: 100%;
    }

    &-head {
      padding: 0 rem(18px);
      margin-bottom: 16px;

      @include width-until($mobile) {
        padding: 0 rem(6px);
      }

      &-title {
        font-size: rem(26px);
        font-weight: 700;
        letter-spacing: 1px;
        margin-bottom: 4px;
      }
    }

    &-scroll-container {
      overflow: auto;
      height: 100%;
    }

    &-scroll-content {
      padding: 0 rem(18px);
    }

    &-close-btn {
      position: absolute;
      top: rem(8px);
      right: rem(10px);
      border-radius: 50%;
      height: rem(24px);
      width: rem(24px);
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;

      i {
        color: $main-font-color;
        font-weight: 600;
        font-size: rem(34px);
      }
    }
  }

  .event-list {
    &__date {
      margin-bottom: rem(14px);
      font-size: rem(14px);
    }

    &__calendar-filter {
      margin-left: auto;
      display: flex;
      justify-content: flex-end;
      align-items: center;

      &-box {
        max-width: 140px;
      }
    }

    &__day {
      margin-bottom: rem(20px);
    }

    &__filters-wrapper {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }

    &__kind-filter {
      &-btn {
        margin-right: 5px;
        margin-bottom: 10px;
        margin-top: 10px;
        position: relative;

        &.--inactive {
          background-color: grey !important;
        }

        i {
          font-size: rem(20px);
        }

        &:hover {
          @include width-from($mobile) {
            .event-list__kind-filter-label {
              opacity: 1;
              pointer-events: auto;
            }
          }
        }

        &-icon {
          background: currentColor;
          width: 26px;
          height: 22px;
        }
      }

      &-label {
        word-wrap: break-spaces;
        letter-spacing: rem(0.7px);
        font-weight: 500;
        line-height: 0.875;
        white-space: nowrap;
        padding: rem(4px) rem(8px);
        border-radius: rem(14px);
        transition: .5s;
        color: $main-font-color;
        background-color: $main-bg-color;
        pointer-events: none;
        font-size: rem(10px);
        font-weight: 700;
        border: 1px solid $main-bg-color;
        z-index: 2;
        bottom: -32px;
        left: 50%;
        transform: translateX(-50%);

        &:hover {
          text-decoration: none;
        }

        @include width-from($mobile) {
          position: absolute;
          opacity: 0;
        }

        @include width-until($mobile) {
          color: $main-font-color;
          background-color: $main-bg-color;
          display: none;
        }
      }
    }
  }

  .text-green input{
    color: green !important;
  }

</style>
